import { useEffect, useState, type ReactNode } from 'react';
import { InlineLoading } from '@carbon/react';
import ErrorToast from 'components/error-toast';
import { InlineEdit } from 'components/inline-edit';
import styles from './center-header.module.scss';

interface CenterHeaderProps {
  /** Workspace name | eval description | '' (package mode / not yet loaded). */
  title: string;
  /** True in workspace OR eval mode → the title is an editable InlineEdit. */
  editable: boolean;
  /** Accessibility label for the editable title ("Workspace name" | "Evaluation name"). */
  label?: string;
  /** Present only when editable → commits the rename with the edited name. */
  onSave?: (name: string) => void;
  /** Rename in flight → shows a small saving indicator. */
  saving?: boolean;
  /** A failed-rename message → reverts the field to the previous name + shows a toast. */
  saveError?: string;
  /** Right-aligned scoped actions (mock §1) — e.g. the Eval Settings toggle. */
  actions?: ReactNode;
  /** Far-left leading control (e.g. the left-pane collapse toggle) — shown in every view. */
  leading?: ReactNode;
}

/**
 * The center title bar (mock §1) — fully presentational. It renders one of three
 * things from the props it's handed and sources no data itself (the title is resolved
 * by `useCenterTitle`, owned by `Eval3NewContent`):
 *   - `editable` → the title as an `InlineEdit` (a workspace OR eval rename), with a
 *     small saving indicator while `saving`;
 *   - `!editable` with a title → a read-only heading (reserved for a non-editable
 *     titled mode; no current caller — package mode passes an empty title);
 *   - empty title → an empty bar (the chrome stays so the center doesn't shift —
 *     package mode).
 *
 * The mock's scoped action buttons (Duplicate / Export / Eval Settings / Save) are out
 * of P3 — name only. `InlineEdit` is controlled, so a local buffer holds the in-progress
 * edit and re-syncs whenever the `title` prop changes (workspace switch / post-rename
 * refresh).
 *
 * @param {CenterHeaderProps} props - The resolved title-bar props.
 * @return {JSX.Element} The center title bar.
 */
export function CenterHeader({
  title,
  editable,
  label,
  onSave,
  saving,
  saveError,
  actions,
  leading,
}: CenterHeaderProps) {
  const [value, setValue] = useState(title);

  // Re-sync the edit buffer when the title changes (a different workspace selected, or
  // the saved name coming back after a rename invalidation).
  useEffect(() => {
    setValue(title);
  }, [title]);

  // On a failed rename, revert the buffer to the previous (server) name — on error the
  // title prop is unchanged (no invalidation), so this resets the unsaved edit. Relies
  // on the caller clearing saveError at save-start: each failure transitions
  // undefined→message, so this effect re-fires even on a repeated identical error.
  useEffect(() => {
    if (saveError) {
      setValue(title);
    }
  }, [saveError, title]);

  return (
    <div className={styles.centerHeader}>
      {leading}
      {/* The title is the flexible, shrink-to-fit element of the bar: it truncates as the
          center column narrows so the pinned action cluster stays fully visible and never
          overflows onto the Eval Settings panel (the alignment fix — see .titleArea). */}
      <div className={styles.titleArea}>
        {editable ? (
          <InlineEdit
            id="eval3-center-title"
            value={value}
            labelText={label ?? 'Name'}
            editLabel="Edit"
            saveLabel="Save"
            cancelLabel="Cancel"
            editAlwaysVisible
            inheritTypography
            size="lg"
            onChange={(next: string) => setValue(next)}
            onCancel={() => setValue(title)}
            // EditInPlace fires onSave on the Save button AND on blur-with-a-value, so
            // skip a save that didn't actually change the name (avoids a no-op PATCH).
            onSave={() => {
              if (value !== title) {
                onSave?.(value);
              }
            }}
          />
        ) : (
          title && <h2 className={styles.title}>{title}</h2>
        )}
        {editable && saving && (
          <InlineLoading className={styles.saving} description="Saving…" />
        )}
      </div>
      {saveError && <ErrorToast key={saveError} message={saveError} />}
      {actions && <div className={styles.actions}>{actions}</div>}
    </div>
  );
}
